import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

interface UpdateDaasSettingsArgs {
}

/*
 * Note: The DaaS settings object originally contained the PCM value,
 * but that has now been handled by finance models.
 * 
 * I'm leaving this in the code in case we ever decide to start re-using the
 * DaaS settings object.
 */
export const useDaasSettings = () => {
  return useAPIQuery(['getSettings'], async client => {
    const response = await client.settings.getDaaSSettings();
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useUpdateDaasSettings = () => {
  return useAPIMutation((client, query) => {
    return async (updateArgs: UpdateDaasSettingsArgs) => {
      const response = await client.settings.updateDaaSSettings({
        body: updateArgs
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getSettings']);
      return response.body;
    };
  });
};

export const usePropertyBagForRole = () => {
  const role = useRole();
  // Consider cache key
  return useAPIQuery([], async client => {
    if (role.type === RoleType.Admin || role.type === RoleType.CreditChecker) return {};

    let response;

    if (role.type === RoleType.Partner) {
      response = await client.partners.getPartner({ params: { id: role.partnerId! } });
    } else {
      response = await client.companies.getCompany({ params: { id: role.customerId!, partnerId: role.partnerId! } });
    }

    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.propertyBag;
  });
};

export const useDisplayPreferences = () => {
  return useAPIQuery(['getDisplayPreferences'], async client => {
    const response = await client.settings.getDisplayPreferences();
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface UpdateDisplayPreferencesArgs {
  orderedCategoryIds?: string[];
  completedTours?: string[];
}

export const useUpdateDisplayPreferences = () => {
  return useAPIMutation((client, query) => {
    return async ({ orderedCategoryIds, completedTours }: UpdateDisplayPreferencesArgs) => {
      // First, fetch current preferences to not overwrite missing fields
      const currentPrefsResponse = await client.settings.getDisplayPreferences();
      const currentPrefs = currentPrefsResponse.status === 200 ? currentPrefsResponse.body : { category: { displayOrderIds: [] }, completedTours: [] };

      const response = await client.settings.updateDisplayPreferences({
        body: {
          category: {
            displayOrderIds: orderedCategoryIds ?? currentPrefs.category.displayOrderIds
          },
          completedTours: completedTours ?? currentPrefs.completedTours
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getDisplayPreferences']);
      await query.invalidateQueries(['getCategories']);
      return response.body;
    };
  });
};
